import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, PackageCheck, ImagePlus } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { todayISO } from '@/lib/utils';
import SignaturePad from '@/components/SignaturePad';
import { resolvePickupQtySqft, toUnit, pickupMaxInUnit, type PickupUnit } from '@/lib/pickupUnits';
import { isAreaUnit } from '@billflow/shared';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PickupableLine {
  estimate_item_id: number;
  item_id: number | null;
  description: string;
  item_name: string | null;
  unit: string | null;
  sold_qty: number;
  already_picked_qty: number;
  remaining_qty: number;
  sqft_per_box: number | null;
  sqft_per_piece: number | null;
}

interface PickupableResponse {
  data: {
    estimate: {
      id: number;
      number: string;
      status: string;
      expected_pickup_date: string | null;
    };
    lines: PickupableLine[];
  };
}

interface CreatePickupResponse {
  data: { pickup: { id: number } };
}

interface RecordPickupModalProps {
  estimateId: number;
  estimateNumber?: string;
  onClose: () => void;
  onSaved: () => void;
}

type Location = 'store' | 'supplier';
type Unit = PickupUnit;

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Compact quantity formatter: integers stay clean, fractions show up to 2dp (sq ft).
// `resolvePickupQtySqft` + `toUnit` live in @/lib/pickupUnits so they're unit-tested.
const fmtQty = (n: number) => (Number.isInteger(n) ? String(n) : Number(n.toFixed(2)).toString());

// Segmented Box | ft² unit toggle — mirrors EstimateFormPage's entry-mode control.
// Greyed + non-interactive when the item has no box size (forced to sq ft).
function UnitToggle({
  unit,
  disabled,
  onChange,
}: {
  unit: Unit;
  disabled: boolean;
  onChange: (u: Unit) => void;
}) {
  return (
    <div
      className={`flex items-center rounded-md overflow-hidden border border-input h-6 w-[68px] ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange('box')}
        className={`flex-1 text-[9px] font-semibold uppercase tracking-wide transition-colors h-full px-1 ${
          unit === 'box'
            ? 'bg-primary text-primary-foreground'
            : 'bg-background text-muted-foreground hover:text-foreground'
        } ${disabled ? 'cursor-not-allowed' : ''}`}
      >
        Box
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange('sqft')}
        className={`flex-1 text-[9px] font-semibold uppercase tracking-wide transition-colors h-full px-1 border-l border-input ${
          unit === 'sqft'
            ? 'bg-primary text-primary-foreground'
            : 'bg-background text-muted-foreground hover:text-foreground'
        } ${disabled ? 'cursor-not-allowed' : ''}`}
      >
        ft²
      </button>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function RecordPickupModal({
  estimateId,
  estimateNumber,
  onClose,
  onSaved,
}: RecordPickupModalProps) {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['pickupable', String(estimateId)],
    queryFn: () => apiFetch<PickupableResponse>(`/api/pickups/pickupable/${estimateId}`),
  });

  // Per-line qty + location + display unit, keyed by estimate_item_id.
  const [qtyById, setQtyById] = useState<Record<number, string>>({});
  const [locById, setLocById] = useState<Record<number, Location>>({});
  const [unitById, setUnitById] = useState<Record<number, Unit>>({});
  const [date, setDate] = useState(todayISO());
  const [receivedBy, setReceivedBy] = useState('');
  const [notes, setNotes] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  // Only goods lines with something left to collect are actionable.
  const actionable = useMemo(
    () => (data?.data.lines ?? []).filter((l) => l.remaining_qty > 1e-9),
    [data],
  );

  function setQty(id: number, value: string) {
    setQtyById((prev) => ({ ...prev, [id]: value }));
  }

  function setLoc(id: number, value: Location) {
    setLocById((prev) => ({ ...prev, [id]: value }));
  }

  function setUnit(id: number, value: Unit) {
    setUnitById((prev) => ({ ...prev, [id]: value }));
  }

  // Resolved display unit for a line: forced to sqft when the item has no box size.
  function unitFor(l: PickupableLine): Unit {
    const spb = l.sqft_per_box ?? 0;
    if (!(spb > 0)) return 'sqft';
    return unitById[l.estimate_item_id] ?? 'box';
  }

  function pickAllRemaining() {
    const next: Record<number, string> = {};
    for (const l of actionable) {
      const spb = l.sqft_per_box ?? 0;
      next[l.estimate_item_id] = fmtQty(toUnit(l.remaining_qty, unitFor(l), spb));
    }
    setQtyById(next);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Build the items payload from non-zero qty lines, validating against remaining.
    // Quantities are entered in the chosen unit (boxes default) but always sent as sq ft.
    const items: Array<{ estimate_item_id: number; qty: number; location: Location }> = [];
    for (const l of actionable) {
      const raw = qtyById[l.estimate_item_id];
      const entered = parseFloat(raw ?? '');
      if (!Number.isFinite(entered) || entered <= 0) continue;
      const unit = unitFor(l);
      const spb = l.sqft_per_box ?? 0;
      const resolved = resolvePickupQtySqft(entered, unit, l.remaining_qty, spb);
      if ('error' in resolved) {
        const limit = fmtQty(toUnit(l.remaining_qty, unit, spb));
        const label = !isAreaUnit(l.unit ?? '') ? '' : unit === 'box' ? ' boxes' : ' sq ft';
        toast.error(`Cannot pick up more than ${limit}${label} for "${l.description}"`);
        return;
      }
      items.push({
        estimate_item_id: l.estimate_item_id,
        qty: resolved.sqft,
        location: locById[l.estimate_item_id] ?? 'store',
      });
    }

    if (items.length === 0) {
      toast.error('Enter a pickup quantity for at least one item');
      return;
    }
    if (!date) {
      toast.error('Select a pickup date');
      return;
    }

    setSaving(true);
    try {
      const res = await apiFetch<CreatePickupResponse>('/api/pickups', {
        method: 'POST',
        body: JSON.stringify({
          estimate_id: estimateId,
          date,
          received_by: receivedBy.trim() || undefined,
          notes: notes.trim() || undefined,
          signature: signature ?? undefined,
          items,
        }),
      });

      // Optional photo: multipart upload mirrors AttachmentsPanel (apiFetch can't do multipart).
      const newId = res.data.pickup.id;
      if (photo) {
        const fd = new FormData();
        fd.append('photo', photo);
        const photoRes = await fetch(`/api/pickups/${newId}/photo`, {
          method: 'POST',
          credentials: 'include',
          body: fd,
        });
        if (!photoRes.ok) {
          const body = await photoRes.json().catch(() => ({}));
          // The pickup itself saved — surface the photo failure without blocking.
          toast.warning(
            `Pickup recorded, but the photo failed to upload: ${
              (body as { error?: string }).error ?? `HTTP ${photoRes.status}`
            }`,
          );
        }
      }

      toast.success('Pickup recorded');
      queryClient.invalidateQueries({ queryKey: ['estimate', String(estimateId)] });
      queryClient.invalidateQueries({ queryKey: ['pickupable', String(estimateId)] });
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to record pickup');
    } finally {
      setSaving(false);
    }
  }

  const title = `Record Pickup${estimateNumber ? ` · ${estimateNumber}` : ''}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg border bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-6">
            {isLoading ? (
              <p className="py-8 text-center text-sm text-muted-foreground">Loading items…</p>
            ) : error ? (
              <p className="text-sm text-destructive">
                {error instanceof Error ? error.message : 'Failed to load items.'}
              </p>
            ) : actionable.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                All goods on this invoice have already been picked up.
              </p>
            ) : (
              <>
                {/* Pick up all remaining */}
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-muted-foreground">
                    Goods to collect ({actionable.length})
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={pickAllRemaining}
                    disabled={saving}
                  >
                    <PackageCheck className="mr-1.5 h-3.5 w-3.5" />
                    Pick up all remaining
                  </Button>
                </div>

                {/* Lines table */}
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                          Description
                        </th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground w-20">
                          Unit
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                          Remaining
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground w-28">
                          Qty
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground w-32">
                          Location
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {actionable.map((l) => {
                        // Box/ft² only applies to area (Sq.ft.) items; Pcs/Bag goods show a
                        // plain quantity with no toggle and no unit suffix.
                        const isArea = isAreaUnit(l.unit ?? '');
                        const spb = l.sqft_per_box ?? 0;
                        const hasBox = isArea && spb > 0;
                        const unit = unitFor(l);
                        const unitLabel = !isArea ? '' : unit === 'box' ? 'box' : 'ft²';
                        const remainingInUnit = toUnit(l.remaining_qty, unit, spb);
                        return (
                          <tr key={l.estimate_item_id} className="border-b last:border-0">
                            <td className="px-3 py-2.5">
                              <div className="font-medium">{l.item_name ?? l.description}</div>
                              {l.item_name && l.description !== l.item_name && (
                                <div className="text-xs text-muted-foreground">{l.description}</div>
                              )}
                              <div className="text-xs text-muted-foreground tabular-nums">
                                Sold {fmtQty(toUnit(l.sold_qty, unit, spb))} · Picked{' '}
                                {fmtQty(toUnit(l.already_picked_qty, unit, spb))}
                                {unitLabel && ` ${unitLabel}`}
                              </div>
                            </td>
                            <td className="px-3 py-2.5">
                              {isArea && (
                                <div className="flex justify-center">
                                  <UnitToggle
                                    unit={unit}
                                    disabled={saving || !hasBox}
                                    onChange={(u) => setUnit(l.estimate_item_id, u)}
                                  />
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums">
                              {fmtQty(remainingInUnit)}
                              {unitLabel && (
                                <span className="ml-1 text-xs text-muted-foreground">
                                  {unitLabel}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2.5">
                              <Input
                                type="number"
                                min="0"
                                step="any"
                                max={pickupMaxInUnit(l.remaining_qty, unit, spb)}
                                value={qtyById[l.estimate_item_id] ?? ''}
                                onChange={(e) => setQty(l.estimate_item_id, e.target.value)}
                                placeholder="0"
                                className="h-8 text-right tabular-nums"
                                disabled={saving}
                              />
                            </td>
                            <td className="px-3 py-2.5">
                              <select
                                value={locById[l.estimate_item_id] ?? 'store'}
                                onChange={(e) =>
                                  setLoc(l.estimate_item_id, e.target.value as Location)
                                }
                                disabled={saving}
                                className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <option value="store">Store</option>
                                <option value="supplier">Supplier</option>
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Meta fields */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="pickup-date">Date</Label>
                    <Input
                      id="pickup-date"
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      disabled={saving}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="pickup-received-by">Received by</Label>
                    <Input
                      id="pickup-received-by"
                      value={receivedBy}
                      onChange={(e) => setReceivedBy(e.target.value)}
                      placeholder="Customer / person collecting"
                      disabled={saving}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="pickup-notes">Notes (optional)</Label>
                  <textarea
                    id="pickup-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. Picked up by son in a pickup truck"
                    rows={2}
                    disabled={saving}
                    className="flex w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Signature (optional)</Label>
                  <SignaturePad onChange={setSignature} />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="pickup-photo">Photo (optional)</Label>
                  <div className="flex items-center gap-3">
                    <label
                      htmlFor="pickup-photo"
                      className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted"
                    >
                      <ImagePlus className="h-3.5 w-3.5" />
                      {photo ? 'Change photo' : 'Add photo'}
                    </label>
                    <input
                      id="pickup-photo"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={saving}
                      onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
                    />
                    {photo && (
                      <span className="truncate text-xs text-muted-foreground">{photo.name}</span>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 border-t px-6 py-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || isLoading || actionable.length === 0}>
              {saving ? 'Saving…' : 'Record Pickup'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
